import { webApiClient } from '../authentication';

/**
 * Entrypoint attribution recorded against the redemption. Must stay a member of web's
 * `GwaRedemptionSource`, or the server drops it to null and the channel is lost.
 */
const NANO_REGISTRATION_SOURCE = 'fi_nano_registration';

/**
 * The coupon, SKUs and renewal terms for this flow, served by web.
 *
 * Deliberately fetched rather than declared here. All of it already lives in web's product config, and
 * a second copy in this repo has no way of noticing when that one changes -- deactivate the coupon and
 * this site would keep sending a dead code, which surfaces to the shopper as a generic payment error.
 *
 * `renewalText` is the clearest case: it is a claim about whether web will schedule the term-end step
 * up to the annual plan, so only web can say it honestly.
 */
interface NanoOfferTerms {
  couponCode: string;
  planSku: string;
  moduleSku: string;
  offerText: string;
  renewalText: string | null;
}

async function fetchOfferTerms(): Promise<NanoOfferTerms | null> {
  try {
    const { data } = await webApiClient.get<NanoOfferTerms>('/api/ecommerce/nano-registration-offer');
    return data.couponCode && data.planSku && data.moduleSku ? data : null;
  } catch {
    return null;
  }
}

/**
 * An offer this shopper is entitled to, resolved server-side.
 *
 * `couponCode` on its own grants nothing. Both alias layers in web require a matching signed
 * `entitlement`, which is what binds a shareable-looking code to this shopper's real context.
 */
export interface CheckoutOffer {
  couponCode: string;
  /** Absent for the generic credit, which is a plain coupon with nothing to prove. */
  entitlement?: string;
  /** What this offer renews at once the gifted term ends, as web reports it. */
  renewalText: string | null;
  /** The plan and hardware this offer buys, so no caller has to know a SKU. */
  planSku: string;
  moduleSku: string;
  /** Free text authored server-side. Never hardcode a duration or amount against it. */
  offerText: string;
  /** Rescue attribution. GWA offers only. */
  gwaProgramId?: number;
  gwaSource?: string;
  rescueName?: string | null;
}

interface RescueOfferResponse {
  slug: string;
  programId: number;
  orgName: string | null;
  offer: string;
  promoCodeAlias: string;
  entitlement: string;
}

/**
 * Pulls the rescue slug out of the GWA shop URL the chip query already returns.
 *
 * web mints that URL as `/rescue/<slug>?fi_c=<token>`, so the slug is the last path segment.
 * Anything not of that shape returns null rather than a guess.
 */
export function rescueSlugFromShopUrl(shopUrl: string): string | null {
  try {
    const { pathname } = new URL(shopUrl);
    const match = pathname.match(/\/rescue\/([^/]+)\/?$/);
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

/**
 * Resolves a rescue's GWA offer straight from web.
 *
 * Deliberately not read from the shop's `gwa_entitlement` cookie: that cookie is scoped to the shop
 * domain so nano cannot see it, and going to the source sidesteps the cookie-domain failures the
 * shop side keeps hitting.
 */
async function fetchRescueOffer(slug: string, terms: NanoOfferTerms): Promise<CheckoutOffer | null> {
  try {
    const { data } = await webApiClient.get<RescueOfferResponse>(`/api/ecommerce/rescue/${encodeURIComponent(slug)}`);
    return {
      couponCode: data.promoCodeAlias,
      entitlement: data.entitlement,
      offerText: data.offer,
      renewalText: terms.renewalText,
      planSku: terms.planSku,
      moduleSku: terms.moduleSku,
      gwaProgramId: data.programId,
      gwaSource: NANO_REGISTRATION_SOURCE,
      rescueName: data.orgName,
    };
  } catch {
    return null;
  }
}

/**
 * The offer for a chip, whoever sold it. One interface so the UI never branches on offer type.
 *
 * A rescue-sourced chip resolves its rescue's GWA offer, which carries the attribution that credits
 * the rescue. Everything else gets the generic registration credit, which needs no fetch at all -- it
 * is a plain coupon, so there is no entitlement to mint and nothing to look up.
 *
 * The GWA branch is tried first deliberately: a rescue adopter is also a nano registrant, and only one
 * coupon can apply, so resolving the generic credit first would silently drop the rescue attribution.
 */
export async function resolveCheckoutOffer(gwaShopUrl: string | null): Promise<CheckoutOffer | null> {
  const terms = await fetchOfferTerms();
  if (!terms) {
    return null;
  }

  const slug = gwaShopUrl ? rescueSlugFromShopUrl(gwaShopUrl) : null;
  const rescueOffer = slug ? await fetchRescueOffer(slug, terms) : null;
  if (rescueOffer) {
    return rescueOffer;
  }

  return {
    couponCode: terms.couponCode,
    offerText: terms.offerText,
    renewalText: terms.renewalText,
    planSku: terms.planSku,
    moduleSku: terms.moduleSku,
  };
}
